import { create } from 'zustand'

/**
 * Presença de "digitando" no chat entre humanos. Estado EFÊMERO em memória
 * (não persiste) alimentado pelos eventos SSE `chat.typing` (ver useChatRealtime)
 * e lido pelo indicador na conversa. Cada entrada tem um timestamp; quem parou
 * de digitar expira sozinho (TYPING_TTL_MS) mesmo sem evento de "parou".
 */

export const TYPING_TTL_MS = 6000

export interface TypingEntry {
  name: string
  at: number
}

interface TypingStore {
  byConv: Record<number, Record<number, TypingEntry>>
  setTyping: (cid: number, userId: number, name: string) => void
}

export const useTypingStore = create<TypingStore>((set) => ({
  byConv: {},
  setTyping: (cid, userId, name) =>
    set((s) => ({
      byConv: {
        ...s.byConv,
        [cid]: { ...(s.byConv[cid] ?? {}), [userId]: { name, at: Date.now() } },
      },
    })),
}))

/** Nomes de quem está digitando AGORA (fresco e diferente do próprio usuário). */
export function freshTypers(
  entries: Record<number, TypingEntry> | undefined,
  selfId: number | null,
  now: number,
  ttl: number = TYPING_TTL_MS,
): string[] {
  if (!entries) return []
  return Object.entries(entries)
    .filter(([uid, e]) => Number(uid) !== selfId && now - e.at < ttl)
    .map(([, e]) => e.name)
}

/** Rótulo amigável: "Fulano está digitando…" / "A e B estão…" / "A e mais N…". */
export function typingLabel(names: string[]): string | null {
  if (names.length === 0) return null
  if (names.length === 1) return `${names[0]} está digitando…`
  if (names.length === 2) return `${names[0]} e ${names[1]} estão digitando…`
  return `${names[0]} e mais ${names.length - 1} estão digitando…`
}
